import { __read } from "tslib";
import React, { forwardRef, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import dayjs from 'dayjs';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import { useUtils as useMuiUtils } from '@material-ui/pickers';
import { runKeyHandler } from '@material-ui/pickers/_shared/hooks/useKeyDown';
import { pick, useEventCallback, usePrevious, useRcPortalWindowContext, useResultRef, } from '../../../../foundation';
import { RcButton } from '../../../Buttons';
import { transitionendSubscriber } from '../../../Transitions/utils';
import { DatePickerHeader, } from './DatePickerHeader';
import { Day } from './Day';
import { CalendarSlideTransitionWrapper, DatePickerWrapper, DayFooterWrapper, DaysWrapper, ProgressContainer, StyledDayLabel, StyledDaysHeader, WeekWrapper, } from './styles';
import { focusDayElement, isSameYearAndMonth, onTransitionEnd, RcDatePickerClasses, useScreenReaderContext, } from './utils';
import { Years } from './Years';
var Calendar = forwardRef(function (_a, ref) {
    var onMonthChange = _a.onMonthChange, size = _a.size, date = _a.date, shouldDisableDate = _a.shouldDisableDate, dateRange = _a.dateRange, todayButtonText = _a.todayButtonText, loadingIndicator = _a.loadingIndicator, disablePast = _a.disablePast, disableFuture = _a.disableFuture, renderDay = _a.renderDay, classes = _a.classes, handleDaySelect = _a.handleDaySelect, getInvalidateDateInRange = _a.getInvalidateDateInRange;
    var utils = useMuiUtils();
    var calendarRef = useRef(null);
    var previousFocusDate = usePrevious(function () { return focusedDate; });
    var previousView = usePrevious(function () { return view; });
    var doc = useRcPortalWindowContext().document;
    var weeks = useRef([]);
    // * `getWeekdays` not set locale, need set locale before get
    dayjs.locale(utils.locale);
    var weekdays = useResultRef(function () { return utils.getWeekdays(); }).current;
    var _b = useMemo(function () {
        var nowDate = utils.date();
        return {
            now: nowDate,
            isTodayDisabled: shouldDisableDate(nowDate),
        };
    }, [shouldDisableDate, utils]), now = _b.now, isTodayDisabled = _b.isTodayDisabled;
    var _c = __read(useState(date), 2), focusedDate = _c[0], setFocusedDate = _c[1];
    var _d = __read(useState('day'), 2), view = _d[0], setView = _d[1];
    var _e = __read(useState('left'), 2), slideDirection = _e[0], setSlideDirection = _e[1];
    var _f = __read(useState(0), 2), loadingQueue = _f[0], setLoadingQueue = _f[1];
    var min = dateRange.min, max = dateRange.max;
    var currentMonthNumber = utils.getMonth(focusedDate);
    var pickClasses = useMemo(function () { return pick(classes, ['header', 'leftArrow', 'rightArrow', 'select']); }, [classes]);
    var sameMonthDate = isSameYearAndMonth({ source: focusedDate, comparing: previousFocusDate || null }, utils);
    if (!sameMonthDate) {
        weeks.current = utils.getWeekArray(focusedDate);
    }
    var viewChange = view !== previousView;
    var pushToLoadingQueue = function () {
        setLoadingQueue(loadingQueue + 1);
    };
    var popFromLoadingQueue = function () {
        setLoadingQueue(loadingQueue <= 0 ? 0 : loadingQueue - 1);
    };
    var handleMonthChange = useEventCallback(function (newMonth, direction) {
        setSlideDirection(direction);
        setFocusedDate(getInvalidateDateInRange(newMonth) || newMonth);
        if (onMonthChange) {
            var returnVal = onMonthChange(newMonth);
            if (returnVal) {
                pushToLoadingQueue();
                returnVal.then(function () {
                    popFromLoadingQueue();
                });
            }
        }
    });
    var handleChangeView = useEventCallback(function () {
        setView(view === 'day' ? 'year' : 'day');
    });
    var disableNextMonth = useMemo(function () {
        var nextStartDay = utils.startOfMonth(utils.getNextMonth(focusedDate));
        return !!getInvalidateDateInRange(nextStartDay);
    }, [focusedDate, getInvalidateDateInRange, utils]);
    var disablePrevMonth = useMemo(function () {
        var prevLatestDay = utils.endOfMonth(utils.getPreviousMonth(focusedDate));
        return !!getInvalidateDateInRange(prevLatestDay);
    }, [focusedDate, getInvalidateDateInRange, utils]);
    var backToToday = useEventCallback(function () {
        handleDaySelect(now);
    });
    var handleKeyDown = useEventCallback(function (event) {
        var moveDay = function (value) {
            var day = utils.addDays(focusedDate, value);
            if (day && !shouldDisableDate(day)) {
                var newMonthNumber = utils.getMonth(day);
                if (newMonthNumber !== currentMonthNumber) {
                    return handleMonthChange(day, newMonthNumber > currentMonthNumber ? 'left' : 'right');
                }
                setFocusedDate(day);
            }
        };
        var goToDate = function (day) {
            setFocusedDate(getInvalidateDateInRange(day) || day);
        };
        var confirmDate = function () {
            event.stopPropagation();
            handleDaySelect(focusedDate);
        };
        runKeyHandler(event.nativeEvent, {
            ArrowUp: function () { return moveDay(-7); },
            ArrowDown: function () { return moveDay(7); },
            ArrowLeft: function () { return moveDay(-1); },
            ArrowRight: function () { return moveDay(1); },
            Home: function () { return goToDate(utils.startOfMonth(focusedDate)); },
            End: function () { return goToDate(utils.endOfMonth(focusedDate)); },
            Enter: confirmDate,
            ' ': confirmDate,
        });
    });
    var _g = useScreenReaderContext(), getBackToTodayAriaLabel = _g.getBackToTodayAriaLabel, getDayAriaLabel = _g.getDayAriaLabel;
    var backToTodayAriaLabel = getBackToTodayAriaLabel === null || getBackToTodayAriaLabel === void 0 ? void 0 : getBackToTodayAriaLabel();
    var header = useMemo(function () { return (React.createElement(StyledDaysHeader, { size: size }, weekdays.map(function (day) { return (React.createElement(StyledDayLabel, { size: size, key: day, variant: "caption" }, day)); }))); }, [size, weekdays]);
    var renderDays = function (week) {
        var selectedDate = date;
        return week.map(function (day) {
            var disabled = shouldDisableDate(day);
            var isDayInCurrentMonth = utils.getMonth(day) === currentMonthNumber;
            var dayText = utils.getDayText(day);
            var selected = utils.isSameDay(day, selectedDate);
            var ariaLabel = getDayAriaLabel === null || getDayAriaLabel === void 0 ? void 0 : getDayAriaLabel(selected, utils.getDatePickerHeaderText(day) + " " + utils.getYearText(day));
            var focused = utils.isSameDay(focusedDate, day);
            var dayComponent = (React.createElement(Day, { size: size, disabled: disabled, current: utils.isSameDay(day, now), hidden: !isDayInCurrentMonth, selected: selected, tabIndex: focused ? 0 : -1, focused: focused, day: day, "aria-label": ariaLabel }, dayText));
            var handleClick = !disabled && isDayInCurrentMonth
                ? function () { return handleDaySelect(day); }
                : undefined;
            return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            React.createElement("div", { role: "cell", key: day.toString(), onClick: handleClick, onKeyPress: handleClick }, renderDay
                ? renderDay(day, focusedDate, isDayInCurrentMonth, dayComponent)
                : dayComponent));
        });
    };
    var renderBody = function () {
        var loadingElement = loadingIndicator ? (loadingIndicator) : (React.createElement(MuiCircularProgress, null));
        switch (view) {
            case 'day':
                return (React.createElement(DaysWrapper, { role: "presentation", "aria-label": " " },
                    header,
                    React.createElement(CalendarSlideTransitionWrapper, { className: classes.calendarSlider, slideDirection: slideDirection, transKey: currentMonthNumber }, loadingQueue > 0 ? (React.createElement(ProgressContainer, { className: classes.progress }, loadingElement)) : (
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                    React.createElement("div", { role: "rowgroup", "data-transition-tag": true, onKeyDown: handleKeyDown }, weeks.current.map(function (week) { return (React.createElement(WeekWrapper, { role: "row", size: size, key: "week-" + week[0].toString() }, renderDays(week))); })))),
                    React.createElement(DayFooterWrapper, { className: classes.footer },
                        React.createElement(RcButton, { "data-focusable": true, variant: "plain", onClick: backToToday, disabled: isTodayDisabled, "aria-label": backToTodayAriaLabel, "data-test-automation-id": "date-picker-today" }, todayButtonText))));
            case 'year':
                return (React.createElement(Years, { date: focusedDate, size: size, minDate: min, maxDate: max, onYearChange: function (day) {
                        setView('day');
                        var validateDate = getInvalidateDateInRange(day) || day;
                        setFocusedDate(validateDate);
                    }, disablePast: disablePast, disableFuture: disableFuture, now: now }));
            default:
                return null;
        }
    };
    // same month anchor switch
    useLayoutEffect(function () {
        if (sameMonthDate && !viewChange) {
            focusDayElement(doc);
        }
    });
    // when view change and current is day also focusDay again
    useLayoutEffect(function () {
        if (previousView && view === 'day' && viewChange) {
            focusDayElement(doc);
        }
    }, [previousView, view, viewChange, doc]);
    useLayoutEffect(function () {
        focusDayElement(doc);
        calendarRef.current = doc.querySelector("." + RcDatePickerClasses.popover + " ." + RcDatePickerClasses.popoverPaper);
        var unsubscribe = transitionendSubscriber(calendarRef.current, function (event) {
            onTransitionEnd(event, doc);
        });
        return function () {
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement(DatePickerWrapper, { size: size, ref: ref },
        React.createElement(DatePickerHeader, { classes: pickClasses, size: size, focusedDate: focusedDate, slideDirection: slideDirection, view: view, onMonthChange: handleMonthChange, onViewChange: handleChangeView, disablePrevMonth: disablePrevMonth, disableNextMonth: disableNextMonth }),
        renderBody()));
});
Calendar.defaultProps = {};
Calendar.displayName = 'RcCalendar';
export { Calendar };
